
export default function filtersComponent() {
    return {
        activeFilters: [],
        isCumulative: false,
        animating: false,
        _reducedMotion: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false,
        expandedParents: new Set(),
        filterHierarchy: {},
        hasVisibleItems: true,
        query: '',
        viewMode: 'grid',
        sortMode: 'featured',
        outsideTypologies: false,
        dropdownOpen: '',
        searchOpen: false,
        selectedTypology: 'all',
        selectedTypologyLabel: 'All',
        selectedSubTypology: '',
        selectedSubTypologyLabel: 'All',
        selectedStatus: '',
        selectedSizeUnit: 'sqft',
        selectedSizeMin: null,
        selectedSizeMax: null,
        sizeRangeMin: null,
        sizeRangeMax: null,
        sizeRangeStep: 1,
        sizeTab: 'residence',
        selectedLotSizeMin: null,
        selectedLotSizeMax: null,
        lotSizeRangeMin: null,
        lotSizeRangeMax: null,
        lotSizeRangeStep: 1,
        statusOptions: [],
        filterLabels: {},
        childTypologyOptionsByParent: {},
        initialDiscoveryState: null,
        visibleCount: 0,
        cachedElements: new Map(),
        parentMap: {},
        sizeDragHandle: '',
        sizeTooltipMode: '',
        sizeTooltipHideTimer: null,
        discoveryLayoutSyncFrame: null,
        discoveryLayoutSyncFollowupFrame: null,
        boundSizeDragMove: null,
        boundSizeDragEnd: null,
        sizeDragListenersActive: false,
        
        init() {
            const { dataset } = this.$el;
            
            // Initialize from URL parameters or dataset
            this.initializeFromURL(dataset);
            this.buildFilterHierarchy();
            this.buildFilterLabels();
            this.buildChildTypologyOptions();
            this.cacheElements();
            this.syncTypologyFromActiveFilters();
            this.hydrateSizeRange(dataset);
            this.hydrateLotSizeRange(dataset);
            if (!this.hasSizeRange() && this.hasLotSizeRange()) {
                this.sizeTab = 'lot';
            }
            this.hydrateFacetOptions();
            this.captureInitialDiscoveryState();
            
            // Initial pass — synchronous so there's no single-frame flash on load.
            this.updateActiveFilterClasses();
            this.updateExpandedStates();
            this.applyDiscovery({ _skipAnimation: true });
            
            this.setupListeners();
        },
        
        initializeFromURL(dataset) {
            const shouldRestoreState = dataset.restoreState === '1';
            const urlParams = new URLSearchParams(window.location.search);
            const urlFilters = urlParams.get('filters');
            const urlExpanded = urlParams.get('expanded');
            const urlCumulative = urlParams.get('cumulative');
            const urlQuery = urlParams.get('q');
            const urlView = urlParams.get('view');
            const urlSort = urlParams.get('sort');
            const urlOutside = urlParams.get('outside');
            const urlStatus = urlParams.get('status');
            const urlSizeUnit = urlParams.get('size_unit');
            const urlSize = urlParams.get('size');
            const urlSizeMin = urlParams.get('size_min');
            const urlSizeMax = urlParams.get('size_max');
            const urlLotSizeMin = urlParams.get('lot_size_min');
            const urlLotSizeMax = urlParams.get('lot_size_max');

            // Decode %2C to comma for correct splitting
            const decodedFilters = urlFilters ? urlFilters.replace(/%2C/gi, ',') : null;
            const decodedExpanded = urlExpanded ? urlExpanded.replace(/%2C/gi, ',') : null;

            // Use URL parameters if available, otherwise fall back to dataset
            if (shouldRestoreState && decodedFilters) {
                this.activeFilters = decodedFilters.split(',');
            } else {
                this.activeFilters = dataset.initialFilter?.trim()?.split(/\s+/) || [];
            }

            if (!this.activeFilters.length) {
                this.activeFilters = ['all'];
            }

            // Restore expanded state from URL
            if (shouldRestoreState && decodedExpanded) {
                this.expandedParents = new Set(decodedExpanded.split(','));
            }

            // Restore cumulative mode from URL
            this.isCumulative = (shouldRestoreState && urlCumulative === '1') || dataset.cumulative === "true";
            this.query = shouldRestoreState ? (urlQuery || '').trim() : '';
            this.viewMode = shouldRestoreState && ['grid', 'list'].includes(urlView) ? urlView : 'grid';
            if (shouldRestoreState) {
                const normalizedSort = urlSort === 'date' ? 'date_desc' : urlSort;
                this.sortMode = ['featured', 'date_desc', 'date_asc', 'az'].includes(normalizedSort) ? normalizedSort : 'featured';
            } else {
                this.sortMode = 'featured';
            }
            this.outsideTypologies = shouldRestoreState && urlOutside === '1';
            this.selectedStatus = shouldRestoreState ? (urlStatus || '').trim() : '';
            this.selectedSizeMin = shouldRestoreState ? this.parseNumericValue(urlSizeMin) : null;
            this.selectedSizeMax = shouldRestoreState
                ? this.parseNumericValue(urlSizeMax ?? urlSize)
                : null;
            this.selectedLotSizeMin = shouldRestoreState ? this.parseNumericValue(urlLotSizeMin) : null;
            this.selectedLotSizeMax = shouldRestoreState ? this.parseNumericValue(urlLotSizeMax) : null;
            const normalizedURLUnit = (urlSizeUnit || '').trim().toLowerCase();
            const normalizedDatasetUnit = (dataset.sizeUnit || '').trim().toLowerCase();
            this.selectedSizeUnit = shouldRestoreState
                ? (normalizedURLUnit === 'sqm' ? 'sqm' : (normalizedDatasetUnit === 'sqm' ? 'sqm' : 'sqft'))
                : (normalizedDatasetUnit === 'sqm' ? 'sqm' : 'sqft');
        },
        
        cacheElements() {
            // Cache frequently accessed elements for better performance
            this.cachedElements.set('wrapper', this.$refs.wrapper);
            this.cachedElements.set('filters', this.$el.querySelectorAll('.filter'));
            this.cachedElements.set('filterParents', this.$el.querySelectorAll('.filter-parent'));
            this.cachedElements.set('filterChildren', this.$el.querySelectorAll('.filter-children'));
            const items = this.getDiscoveryItems();
            items.forEach((item, index) => {
                if (!item.dataset.originalIndex) {
                    item.dataset.originalIndex = String(index);
                }
            });
        },
        
        setupListeners() {
            // Use passive listeners for better performance
            document.addEventListener('keydown', (e) => {
                if (e.key !== 'Escape') return;
                if (this.dropdownOpen) {
                    this.dropdownOpen = '';
                }
                if (this.searchOpen) {
                    this.searchOpen = false;
                }
                if (this.expandedParents.size > 0) {
                    this.expandedParents.clear();
                    this.updateExpandedStates();
                }
            }, { passive: true });

            // Native fallback bindings for select controls in case Alpine inline handlers are skipped.
            const typologySelect = this.$el.querySelector('[id^="typology-filter-"]');
            if (typologySelect && !typologySelect.dataset.boundDiscovery) {
                typologySelect.dataset.boundDiscovery = '1';
                typologySelect.addEventListener('change', (event) => {
                    this.setTypologyFilter(event.target.value || 'all');
                });
            }

            const subTypologySelect = this.$el.querySelector('[id^="subtypology-filter-"]');
            if (subTypologySelect && !subTypologySelect.dataset.boundDiscovery) {
                subTypologySelect.dataset.boundDiscovery = '1';
                subTypologySelect.addEventListener('change', (event) => {
                    this.setSubTypologyFilter(event.target.value || '');
                });
            }

            // Fallback for custom dropdown button options.
            this.$el.addEventListener('click', (event) => {
                const target = event.target instanceof Element ? event.target : null;
                if (!target) return;

                const statusOption = target.closest('[data-status-value]');
                if (statusOption && this.$el.contains(statusOption)) {
                    this.selectStatusOption(statusOption.getAttribute('data-status-value') || '');
                    return;
                }

            });

            if (!this.boundSizeDragMove) {
                this.boundSizeDragMove = this.handleSizeDragMove.bind(this);
            }
            if (!this.boundSizeDragEnd) {
                this.boundSizeDragEnd = this.handleSizeDragEnd.bind(this);
            }
        },

        addSizeDragListeners() {
            if (this.sizeDragListenersActive) {
                return;
            }

            this.sizeDragListenersActive = true;
            window.addEventListener('mousemove', this.boundSizeDragMove, { passive: false });
            window.addEventListener('mouseup', this.boundSizeDragEnd, { passive: true });
            window.addEventListener('touchmove', this.boundSizeDragMove, { passive: false });
            window.addEventListener('touchend', this.boundSizeDragEnd, { passive: true });
            window.addEventListener('touchcancel', this.boundSizeDragEnd, { passive: true });
        },

        removeSizeDragListeners() {
            if (!this.sizeDragListenersActive) {
                return;
            }

            this.sizeDragListenersActive = false;
            window.removeEventListener('mousemove', this.boundSizeDragMove);
            window.removeEventListener('mouseup', this.boundSizeDragEnd);
            window.removeEventListener('touchmove', this.boundSizeDragMove);
            window.removeEventListener('touchend', this.boundSizeDragEnd);
            window.removeEventListener('touchcancel', this.boundSizeDragEnd);
        },

        toggleDropdown(name) {
            this.dropdownOpen = this.dropdownOpen === name ? '' : name;
        },

        closeDropdown(name = '') {
            if (!name || this.dropdownOpen === name) {
                this.dropdownOpen = '';
            }
        },

        toggleSearch() {
            this.searchOpen = !(this.searchOpen || this.query.length > 0);
            if (this.searchOpen) {
                requestAnimationFrame(() => {
                    this.$refs?.inlineSearchInput?.focus();
                });
            }
        },
        
        buildFilterHierarchy() {
            const hierarchyData = this.$el.dataset.filterHierarchy;

            if (hierarchyData) {
                try {
                    const cleanedData = hierarchyData
                        .trim()
                        .replace(/,\s*]/g, ']')
                        .replace(/,\s*}/g, '}');

                    this.filterHierarchy = JSON.parse(cleanedData);
                } catch (e) {
                    this.filterHierarchy = {};
                }
            } else {
                this.filterHierarchy = {};
            }
            // Build reverse map child -> parent for quick lookup
            this.parentMap = {};
            Object.keys(this.filterHierarchy).forEach(parent => {
                (this.filterHierarchy[parent] || []).forEach(child => {
                    this.parentMap[child] = parent;
                });
            });
        },

        buildFilterLabels() {
            const labelsData = this.$el.dataset.filterLabels;
            if (!labelsData) {
                this.filterLabels = {};
                return;
            }

            try {
                const cleanedData = labelsData
                    .trim()
                    .replace(/,\s*}/g, '}');
                this.filterLabels = JSON.parse(cleanedData);
            } catch (e) {
                this.filterLabels = {};
            }
        },

        buildChildTypologyOptions() {
            const childrenData = this.$el.dataset.filterChildren;
            if (!childrenData) {
                this.childTypologyOptionsByParent = {};
                return;
            }

            try {
                const cleanedData = childrenData
                    .trim()
                    .replace(/,\s*]/g, ']')
                    .replace(/,\s*}/g, '}');
                const parsed = JSON.parse(cleanedData);
                this.childTypologyOptionsByParent = parsed && typeof parsed === 'object' ? parsed : {};
            } catch (e) {
                this.childTypologyOptionsByParent = {};
            }
        },

        parseDatasetArray(value) {
            if (!value) {
                return [];
            }

            try {
                const parsed = JSON.parse(value);
                return Array.isArray(parsed)
                    ? parsed.map((item) => (item || '').toString().trim()).filter(Boolean)
                    : [];
            } catch (e) {
                return [];
            }
        },

        parseDatasetObject(value) {
            if (!value) {
                return {};
            }

            try {
                const parsed = JSON.parse(value);
                return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
                    ? parsed
                    : {};
            } catch (e) {
                return {};
            }
        },

        parseFilterClasses(filterClasses) {
            if (typeof filterClasses === 'string') {
                return filterClasses.trim().split(/\s+/).filter((filter) => filter.length > 0);
            }

            if (Array.isArray(filterClasses)) {
                return filterClasses.map((filter) => (filter || '').toString().trim()).filter(Boolean);
            }

            return [];
        },

        getFacetDisplayValue(value) {
            const raw = (value || '').toString().trim();
            if (!raw) {
                return '';
            }

            const match = raw.match(/^(.*)\(([^()]+)\)\s*$/);
            if (match && match[2]) {
                const display = match[2].trim();
                if (display) {
                    return display;
                }
            }

            return raw;
        },

        normalizeOptionMapKey(value) {
            return (value || '')
                .toString()
                .toLowerCase()
                .replace(/\s*\([^()]+\)\s*$/, '')
                .replace(/\s+/g, ' ')
                .trim();
        },

        getMappedFacetDisplayValue(value, map = {}) {
            const raw = (value || '').toString().trim();
            if (!raw) {
                return '';
            }

            if (map && typeof map === 'object') {
                const direct = map[raw];
                if (typeof direct === 'string' && direct.trim()) {
                    return direct.trim();
                }

                const normalized = map[this.normalizeOptionMapKey(raw)];
                if (typeof normalized === 'string' && normalized.trim()) {
                    return normalized.trim();
                }
            }

            return this.getFacetDisplayValue(raw);
        },

        getOrderedFacetValues(availableValues, configuredValues = []) {
            if (!(availableValues instanceof Set)) {
                return [];
            }

            if (!configuredValues.length) {
                return Array.from(availableValues).sort((a, b) => a.localeCompare(b));
            }

            const ordered = configuredValues.filter((value) => availableValues.has(value));
            const configuredSet = new Set(configuredValues);
            const extras = Array.from(availableValues)
                .filter((value) => !configuredSet.has(value))
                .sort((a, b) => a.localeCompare(b));

            return ordered.length > 0 || extras.length > 0
                ? [...ordered, ...extras]
                : [...configuredValues];
        },

        updateURLState() {
            const url = new URL(window.location);
            
            // Clear existing filter parameters
            url.searchParams.delete('filters');
            url.searchParams.delete('expanded');
            url.searchParams.delete('cumulative');
            url.searchParams.delete('q');
            url.searchParams.delete('view');
            url.searchParams.delete('sort');
            url.searchParams.delete('outside');
            url.searchParams.delete('status');
            url.searchParams.delete('size_unit');
            url.searchParams.delete('size');
            url.searchParams.delete('size_min');
            url.searchParams.delete('size_max');
            url.searchParams.delete('lot_size_min');
            url.searchParams.delete('lot_size_max');
            url.searchParams.delete('location');
            
            // Add current filter state
            if (this.activeFilters.length > 0 && !this.activeFilters.includes('all')) {
                url.searchParams.set('filters', this.activeFilters.join(','));
            }
            
            if (this.expandedParents.size > 0) {
                url.searchParams.set('expanded', Array.from(this.expandedParents).join(','));
            }
            
            if (this.isCumulative) {
                url.searchParams.set('cumulative', '1');
            }

            if (this.query.trim()) {
                url.searchParams.set('q', this.query.trim());
            }

            if (this.viewMode !== 'grid') {
                url.searchParams.set('view', this.viewMode);
            }

            if (this.sortMode !== 'featured') {
                url.searchParams.set('sort', this.sortMode);
            }

            if (this.outsideTypologies) {
                url.searchParams.set('outside', '1');
            }

            if (this.selectedStatus) {
                url.searchParams.set('status', this.selectedStatus);
            }

            if (this.selectedSizeUnit !== 'sqft') {
                url.searchParams.set('size_unit', this.selectedSizeUnit);
            }

            if (this.isResidenceSizeFilterActive()) {
                url.searchParams.set('size_min', this.selectedSizeMinURLValue());
                url.searchParams.set('size_max', this.selectedSizeMaxURLValue());
            }

            if (this.isLotSizeFilterActive()) {
                url.searchParams.set('lot_size_min', this.serializeSizeValue(this.selectedLotSizeMin));
                url.searchParams.set('lot_size_max', this.serializeSizeValue(this.selectedLotSizeMax));
            }

            // Update URL without triggering page reload
            const newUrl = url.searchParams.toString() ? 
                `${url.pathname}?${url.searchParams.toString()}` : 
                url.pathname;
                
            window.history.replaceState({}, '', newUrl);
        },
        
        setFilter(filter, isChild = false, parentFilter = null) {
            if (filter === "all") {
                this.activeFilters = ["all"];
                this.expandedParents.clear();
            } else if (isChild) {
                this.handleChildFilter(filter, parentFilter);
            } else {
                this.handleParentFilter(filter);
            }

            this.updateActiveFilterClasses();
            this.updateExpandedStates();
            this.syncTypologyFromActiveFilters();
            this.applyDiscovery({ scrollToResults: true });
        },
        
        delay(ms) {
            return new Promise(resolve => setTimeout(resolve, ms));
        },
        
        handleParentFilter(filter) {
            const hasChildren = this.filterHierarchy[filter]?.length > 0;
            
            // Remove "all" from activeFilters
            this.activeFilters = this.activeFilters.filter(f => f !== "all");
            
            if (hasChildren) {
                if (this.expandedParents.has(filter)) {
                    // Collapse
                    this.expandedParents.delete(filter);
                    this.activeFilters = this.activeFilters.filter(f => {
                        return f !== filter && !this.filterHierarchy[filter]?.includes(f);
                    });
                } else {
                    // Expand
                    this.clearOtherParentFilters(filter);
                    this.expandedParents.clear();
                    this.expandedParents.add(filter);
                    this.activeFilters.push(filter);
                }
            } else {
                // Regular filter without children
                this.expandedParents.clear();
                
                if (this.isCumulative) {
                    const index = this.activeFilters.indexOf(filter);
                    if (index > -1) {
                        this.activeFilters.splice(index, 1);
                    } else {
                        this.activeFilters.push(filter);
                    }
                } else {
                    this.activeFilters = [filter];
                }
            }
            
            // Fallback to "all" if no filters active
            if (this.activeFilters.length === 0 && this.expandedParents.size === 0) {
                this.activeFilters = ["all"];
            }
        },
        
        clearOtherParentFilters(currentFilter) {
            const allOtherParentFilters = [];
            Object.entries(this.filterHierarchy).forEach(([parentFilter, children]) => {
                if (parentFilter !== currentFilter) {
                    allOtherParentFilters.push(parentFilter, ...children);
                }
            });
            this.activeFilters = this.activeFilters.filter(f => !allOtherParentFilters.includes(f));
        },
        
        handleChildFilter(filter, parentFilter) {
            if (parentFilter) {
                this.expandedParents.add(parentFilter);
            }
            
            if (this.isCumulative) {
                const index = this.activeFilters.indexOf(filter);
                if (index > -1) {
                    this.activeFilters.splice(index, 1);
                } else {
                    this.activeFilters = [...this.activeFilters.filter(f => f !== "all"), filter];
                }
            } else {
                this.activeFilters = [filter];
            }
            
            if (this.activeFilters.length === 0) {
                this.activeFilters = ["all"];
                this.expandedParents.clear();
            }
        },
        
        isParentExpanded(filter) {
            return this.expandedParents.has(filter)
                || this.activeFilters.includes(filter)
                || this.hasActiveChildren(filter);
        },
        
        isVisible(filterClasses) {
            if (this.activeFilters.includes("all")) {
                return true;
            }
            
            if (!filterClasses || filterClasses.length === 0) {
                return false;
            }
            
            // FIXED: Convert string to array and trim for exact matching
            const filterArray = typeof filterClasses === 'string' 
                ? filterClasses.trim().split(/\s+/).filter(f => f.length > 0)
                : Array.isArray(filterClasses) 
                    ? filterClasses 
                    : [];
            
            if (filterArray.length === 0) {
                return false;
            }
            
            // Check for exact matches in active filters
            for (const activeFilter of this.activeFilters) {
                // Direct match
                if (filterArray.includes(activeFilter)) {
                    return true;
                }
                
                // Check if any children of this parent filter match
                const childFilters = this.filterHierarchy[activeFilter] || [];
                if (childFilters.some(child => filterArray.includes(child))) {
                    return true;
                }
            }
            
            return false;
        },

        isVisibleByDiscovery(filterClasses, element = null) {
            const filterArray = typeof filterClasses === 'string'
                ? filterClasses.trim().split(/\s+/).filter(f => f.length > 0)
                : Array.isArray(filterClasses)
                    ? filterClasses
                    : [];

            const typologyVisible = this.matchesSelectedTypology(filterArray);
            const filterVisible = this.outsideTypologies
                ? (typologyVisible ? false : this.isOutsideTypology(filterArray))
                : typologyVisible;

            if (!filterVisible) {
                return false;
            }

            if (this.selectedStatus) {
                const statusValue = (element?.dataset?.status || '').trim();
                if (!this.matchesFacetValue(statusValue, this.selectedStatus)) {
                    return false;
                }
            }

            if (this.hasActiveSizeFilter()) {
                const sizeValue = this.getElementSizeValue(element);
                if (!this.matchesSizeValue(sizeValue)) {
                    return false;
                }
            }

            if (this.hasActiveLotSizeFilter()) {
                const lotSizeValue = element?.dataset?.lotSizeValue;
                if (!this.matchesLotSizeValue(lotSizeValue)) {
                    return false;
                }
            }

            const q = this.query.trim().toLowerCase();
            if (!q) {
                return true;
            }

            const haystack = element && element.dataset
                ? `${element.dataset.search || ''} ${element.dataset.title || ''} ${element.dataset.summary || ''}`.toLowerCase()
                : '';

            return haystack.includes(q);
        },

        isOutsideTypology(filterArray) {
            if (!filterArray || filterArray.length === 0) {
                return true;
            }

            if (this.activeFilters.includes('all') || this.activeFilters.length === 0) {
                return false;
            }

            return !this.matchesAnyActiveFilter(filterArray);
        },

        matchesAnyActiveFilter(filterArray) {
            for (const activeFilter of this.activeFilters) {
                if (filterArray.includes(activeFilter)) {
                    return true;
                }
                const childFilters = this.filterHierarchy[activeFilter] || [];
                if (childFilters.some(child => filterArray.includes(child))) {
                    return true;
                }
            }
            return false;
        },

        applyDiscovery(options = {}) {
            // State update is ALWAYS synchronous. This is the only reliable way to
            // ensure card visibility is correct before any paint — regardless of
            // Alpine's reactive effect scheduling or rAF ordering.
            this._applyDiscoveryInstant(options);

            const wrapper = this.cachedElements.get('wrapper');
            const animate = !options._skipAnimation && !this._reducedMotion && wrapper;
            if (!animate) return;

            // State is already correct. Now play a cosmetic fade-in:
            // set opacity 0 instantly, then let the CSS transition fade back to 1.
            // Force a reflow between add/remove so the browser registers the
            // opacity-0 as a true starting point for the transition.
            wrapper.classList.add('is-filtering');
            void wrapper.offsetHeight; // force reflow → browser records opacity:0
            requestAnimationFrame(() => {
                wrapper.classList.remove('is-filtering'); // CSS transition plays
                this.animating = false;
            });
        },

        _applyDiscoveryInstant(options = {}) {
            this.sortItems();
            this.updateItemVisibility();
            this.updateVisibleCount();
            this.hasVisibleItems = this.visibleCount > 0;
            this.updateProjectLinks();
            this.updateURLState();
            this.notifyDiscoveryLayoutChange();
            if (options.scrollToResults) this.scrollResultsIntoView();
        },

        notifyDiscoveryLayoutChange() {
            if (this.discoveryLayoutSyncFrame) {
                cancelAnimationFrame(this.discoveryLayoutSyncFrame);
            }
            if (this.discoveryLayoutSyncFollowupFrame) {
                cancelAnimationFrame(this.discoveryLayoutSyncFollowupFrame);
            }

            this.discoveryLayoutSyncFrame = requestAnimationFrame(() => {
                this.discoveryLayoutSyncFollowupFrame = requestAnimationFrame(() => {
                    this.discoveryLayoutSyncFrame = null;
                    this.discoveryLayoutSyncFollowupFrame = null;

                    window.dispatchEvent(new CustomEvent('project-discovery:layout-change', {
                        detail: {
                            viewMode: this.viewMode,
                        },
                    }));
                });
            });
        },

        scrollResultsIntoView() {
            const shell = this.$el.querySelector('.discovery-shell');
            if (!shell) return;

            const shellRect = shell.getBoundingClientRect();
            const isSticky = shellRect.top <= 0 && shellRect.bottom > 0;
            if (!isSticky) {
                return;
            }

            const firstVisibleCard = this.getDiscoveryItems().find(
                (item) => item.dataset.discoveryVisible !== '0'
            );
            if (!firstVisibleCard) return;

            const topGap = 16;
            const desiredCardTop = shellRect.bottom + topGap;
            const getTargetY = () => Math.max(
                0,
                window.scrollY + firstVisibleCard.getBoundingClientRect().top - desiredCardTop
            );

            requestAnimationFrame(() => {
                const targetY = getTargetY();
                if (Math.abs(window.scrollY - targetY) < 2) {
                    return;
                }
                window.scrollTo({
                    top: targetY,
                    behavior: 'smooth',
                });
            });
        },

        getDiscoveryItems() {
            const wrapper = this.cachedElements.get('wrapper');
            if (!wrapper) return [];
            return Array.from(wrapper.querySelectorAll('[data-discovery-item]'));
        },

        updateVisibleCount() {
            const items = this.getDiscoveryItems();
            this.visibleCount = items.filter(item => !item.classList.contains('is-hidden-by-filter')).length;
        },

        updateItemVisibility() {
            const items = this.getDiscoveryItems();
            items.forEach(item => {
                const wasHidden = item.classList.contains('is-hidden-by-filter') || item.dataset.discoveryVisible === '0';
                const isVisible = this.isVisibleByDiscovery(item.dataset.filter || '', item);
                item.classList.toggle('is-hidden-by-filter', !isVisible);
                item.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
                item.style.display = isVisible ? '' : 'none';
                item.dataset.discoveryVisible = isVisible ? '1' : '0';
            });
        },

        sortItems() {
            const items = this.getDiscoveryItems();
            if (!items.length) return;

            const sorted = [...items].sort((a, b) => {
                if (this.sortMode === 'az') {
                    const aTitle = (a.dataset.title || a.dataset.search || '').toLowerCase();
                    const bTitle = (b.dataset.title || b.dataset.search || '').toLowerCase();
                    return aTitle.localeCompare(bTitle);
                }

                if (this.sortMode === 'date_desc' || this.sortMode === 'date_asc') {
                    const aDate = Date.parse(a.dataset.created || '') || 0;
                    const bDate = Date.parse(b.dataset.created || '') || 0;
                    if (aDate !== bDate) return this.sortMode === 'date_asc' ? aDate - bDate : bDate - aDate;
                }

                const aIndex = parseInt(a.dataset.originalIndex || '0', 10);
                const bIndex = parseInt(b.dataset.originalIndex || '0', 10);
                return aIndex - bIndex;
            });

            // Use CSS `order` instead of moving DOM nodes. Moving nodes triggers
            // Alpine's MutationObserver which tears down and re-runs x-show effects —
            // creating a race condition with updateItemVisibility(). CSS order
            // achieves the same visual result with zero DOM mutations.
            sorted.forEach((item, index) => {
                item.style.order = String(index);
            });
        },

        clearSearch() {
            this.query = '';
            this.searchOpen = false;
            this.applyDiscovery({ scrollToResults: true });
        },

        setViewMode(mode) {
            this.viewMode = mode === 'list' ? 'list' : 'grid';
            this.applyDiscovery({ scrollToResults: true });
        },

        setTypologyFilter(filter) {
            const next = (filter || 'all').trim();
            this.selectedTypology = next || 'all';
            this.selectedSubTypology = '';
            this.selectedSubTypologyLabel = 'All';
            this.outsideTypologies = false;
            this.syncActiveFiltersFromTypology();
            this.applyDiscovery({ scrollToResults: true });
        },

        selectTypologyOption(filter, label = '') {
            this.closeDropdown();
            this.selectedTypologyLabel = (label || '').trim() || this.getFilterLabel(filter);
            this.setTypologyFilter(filter);
        },

        setSubTypologyFilter(filter) {
            const selectedFilter = filter || '';
            this.selectedSubTypology = selectedFilter;
            this.outsideTypologies = false;
            this.syncActiveFiltersFromTypology();
            this.applyDiscovery({ scrollToResults: true });
        },

        selectSubTypologyOption(filter, label = '') {
            this.closeDropdown();
            this.selectedSubTypologyLabel = (label || '').trim() || 'All';
            this.setSubTypologyFilter(filter);
        },

        selectStatusOption(value) {
            this.closeDropdown();
            this.selectedStatus = (value || '').trim();
            this.applyDiscovery({ scrollToResults: true });
        },

        updateSizeMinSlider(value) {
            const numericValue = this.clampSizeValue(this.parseNumericValue(value), this.sizeRangeMin, this.selectedSizeMax);
            this.selectedSizeMin = numericValue;
            if (this.selectedSizeMax < this.selectedSizeMin) {
                this.selectedSizeMax = this.selectedSizeMin;
            }
            this.showSizeTooltip('min');
            this.applyDiscovery({ scrollToResults: true });
        },

        updateSizeMaxSlider(value) {
            const numericValue = this.clampSizeValue(this.parseNumericValue(value), this.selectedSizeMin, this.sizeRangeMax);
            this.selectedSizeMax = numericValue;
            if (this.selectedSizeMin > this.selectedSizeMax) {
                this.selectedSizeMin = this.selectedSizeMax;
            }
            this.showSizeTooltip('max');
            this.applyDiscovery({ scrollToResults: true });
        },

        startSizeDrag(handle, event) {
            const isLot = this.sizeTab === 'lot';
            if (isLot ? !this.hasLotSizeRange() : !this.hasSizeRange()) {
                return;
            }

            this.sizeDragHandle = handle === 'max' ? 'max' : 'min';
            this.addSizeDragListeners();
            this.showSizeTooltip(this.sizeDragHandle);
            this.updateSizeFromPointer(event);
        },

        handleSizeDragMove(event) {
            if (!this.sizeDragHandle) {
                return;
            }

            this.showSizeTooltip(this.sizeDragHandle);
            this.updateSizeFromPointer(event);
        },

        handleSizeDragEnd() {
            if (!this.sizeDragHandle) {
                this.removeSizeDragListeners();
                return;
            }

            this.sizeDragHandle = '';
            this.removeSizeDragListeners();
            this.hideSizeTooltipSoon();
            // Sync is-hidden-by-filter state, counts, sort order and URL after drag.
            // Skip animation — Alpine's x-show already moved cards in real-time during drag,
            // so running FLIP now would conflict with the already-updated DOM state.
            this.applyDiscovery({ scrollToResults: false, _skipAnimation: true });
        },

        handleSizeTrackClick(event) {
            const isLot = this.sizeTab === 'lot';
            if (isLot ? !this.hasLotSizeRange() : !this.hasSizeRange()) {
                return;
            }

            const pointerValue = this.getSizeValueFromPointer(event);
            if (pointerValue === null) {
                return;
            }

            const currentMin = isLot ? this.selectedLotSizeMin : this.selectedSizeMin;
            const currentMax = isLot ? this.selectedLotSizeMax : this.selectedSizeMax;
            const distanceToMin = Math.abs(pointerValue - currentMin);
            const distanceToMax = Math.abs(pointerValue - currentMax);
            this.sizeDragHandle = distanceToMin <= distanceToMax ? 'min' : 'max';
            this.showSizeTooltip(this.sizeDragHandle);
            this.updateSizeFromPointer(event);
            this.sizeDragHandle = '';
            this.hideSizeTooltipSoon();
            this.applyDiscovery({ scrollToResults: false });
        },

        updateSizeFromPointer(event) {
            const pointerValue = this.getSizeValueFromPointer(event);
            if (pointerValue === null) {
                return;
            }

            if (event.cancelable) {
                event.preventDefault();
            }

            const isLot = this.sizeTab === 'lot';
            if (isLot) {
                if (this.sizeDragHandle === 'min') {
                    this.selectedLotSizeMin = this.clampLotSizeValue(pointerValue, this.lotSizeRangeMin, this.selectedLotSizeMax);
                } else if (this.sizeDragHandle === 'max') {
                    this.selectedLotSizeMax = this.clampLotSizeValue(pointerValue, this.selectedLotSizeMin, this.lotSizeRangeMax);
                }
            } else {
                if (this.sizeDragHandle === 'min') {
                    this.selectedSizeMin = this.clampSizeValue(pointerValue, this.sizeRangeMin, this.selectedSizeMax);
                } else if (this.sizeDragHandle === 'max') {
                    this.selectedSizeMax = this.clampSizeValue(pointerValue, this.selectedSizeMin, this.sizeRangeMax);
                }
            }

            // Call updateItemVisibility directly — synchronous, no rAF, no microtask batching.
            // This gives immediate card show/hide as the handle moves.
            // applyDiscovery on drag end syncs counts, sort, and URL.
            this.updateItemVisibility();
        },

        setSizeUnit(unit) {
            const normalizedUnit = (unit || '').trim().toLowerCase() === 'sqm' ? 'sqm' : 'sqft';
            if (this.selectedSizeUnit === normalizedUnit) {
                return;
            }
            this.selectedSizeUnit = normalizedUnit;
            this.resetSizeFilter(false);
            this.applyDiscovery({ scrollToResults: true });
        },

        toggleSizeUnit() {
            this.setSizeUnit(this.selectedSizeUnit === 'sqm' ? 'sqft' : 'sqm');
        },

        resetSizeFilter(shouldApply = true) {
            if (!this.hasSizeRange()) {
                return;
            }
            this.selectedSizeMin = this.sizeRangeMin;
            this.selectedSizeMax = this.sizeRangeMax;
            this.showSizeTooltip('both');
            this.hideSizeTooltipSoon();
            if (shouldApply) {
                this.applyDiscovery({ scrollToResults: true });
            }
        },

        selectSortOption(value) {
            this.closeDropdown();
            this.sortMode = ['featured', 'date_desc', 'date_asc', 'az'].includes(value) ? value : 'featured';
            this.applyDiscovery({ scrollToResults: true });
        },

        hasResetAvailable() {
            const base = this.initialDiscoveryState;
            if (!base) {
                return false;
            }
            return (
                this.viewMode !== base.viewMode ||
                this.sortMode !== base.sortMode ||
                this.selectedTypology !== base.selectedTypology ||
                this.selectedSubTypology !== base.selectedSubTypology ||
                this.selectedStatus !== base.selectedStatus ||
                this.selectedSizeUnit !== base.selectedSizeUnit ||
                this.selectedSizeMinURLValue() !== this.serializeSizeValue(base.selectedSizeMin) ||
                this.selectedSizeMaxURLValue() !== this.serializeSizeValue(base.selectedSizeMax) ||
                this.serializeSizeValue(this.selectedLotSizeMin) !== this.serializeSizeValue(base.selectedLotSizeMin) ||
                this.serializeSizeValue(this.selectedLotSizeMax) !== this.serializeSizeValue(base.selectedLotSizeMax) ||
                this.query.trim() !== base.query
            );
        },

        resetDiscovery() {
            const base = this.initialDiscoveryState || {
                activeFilters: ['all'],
                query: '',
                viewMode: 'grid',
                sortMode: 'featured',
                selectedTypology: 'all',
                selectedSubTypology: '',
                selectedStatus: '',
                selectedSizeUnit: 'sqft',
                selectedSizeMin: this.sizeRangeMin,
                selectedSizeMax: this.sizeRangeMax,
                selectedLotSizeMin: this.lotSizeRangeMin,
                selectedLotSizeMax: this.lotSizeRangeMax,
            };

            this.query = base.query;
            this.searchOpen = false;
            this.viewMode = base.viewMode;
            this.sortMode = base.sortMode;
            this.outsideTypologies = false;
            this.selectedTypology = base.selectedTypology;
            this.selectedTypologyLabel = this.getFilterLabel(this.selectedTypology);
            this.selectedSubTypology = base.selectedSubTypology;
            this.selectedSubTypologyLabel = this.selectedSubTypology
                ? this.getFilterLabel(this.selectedSubTypology)
                : 'All';
            this.selectedStatus = base.selectedStatus;
            this.selectedSizeUnit = base.selectedSizeUnit;
            this.selectedSizeMin = base.selectedSizeMin;
            this.selectedSizeMax = base.selectedSizeMax;
            this.selectedLotSizeMin = base.selectedLotSizeMin;
            this.selectedLotSizeMax = base.selectedLotSizeMax;
            this.activeFilters = Array.isArray(base.activeFilters) && base.activeFilters.length
                ? [...base.activeFilters]
                : ['all'];
            this.syncTypologyFromActiveFilters();
            this.applyDiscovery({ scrollToResults: true });
        },
        
        checkVisibleItems() {
            this.updateVisibleCount();
            this.hasVisibleItems = this.visibleCount > 0;
        },
        
        updateProjectLinks() {
            const wrapper = this.cachedElements.get('wrapper');
            if (!wrapper) return;
            
            const selectors = [
                'a[href*="/work/"]',
                'a[href*="/projects/"]',
                '.project-link',
                '.card a',
                'article a',
                '[class*="filter-"] a'
            ];
            
            let projectLinks = [];
            
            for (const selector of selectors) {
                const found = wrapper.querySelectorAll(selector);
                if (found.length > 0) {
                    projectLinks = Array.from(found);
                    break;
                }
            }
            
            if (projectLinks.length === 0) return;
            
            // Batch link updates
            const filterParam = this.buildFilterParam();
            
            projectLinks.forEach(link => {
                const originalHref = link.getAttribute('data-original-href') || link.href;
                
                if (!link.getAttribute('data-original-href')) {
                    link.setAttribute('data-original-href', originalHref);
                }
                
                if (filterParam) {
                    const separator = originalHref.includes('?') ? '&' : '?';
                    link.href = `${originalHref}${separator}${filterParam}`;
                } else {
                    link.href = originalHref;
                }
            });
        },
        
        buildFilterParam() {
            if (this.activeFilters.length === 0 || this.activeFilters.includes('all')) {
                if (
                    !this.query &&
                    !this.outsideTypologies &&
                    this.sortMode === 'featured' &&
                    this.viewMode === 'grid' &&
                    !this.selectedStatus &&
                    this.selectedSizeUnit === 'sqft' &&
                    !this.hasActiveSizeFilter()
                ) {
                    return '';
                }
            }
            
            const params = new URLSearchParams();
            if (this.activeFilters.length > 0 && !this.activeFilters.includes('all')) {
                params.set('filters', this.activeFilters.join(','));
            }
            
            if (this.expandedParents.size > 0) {
                params.set('expanded', Array.from(this.expandedParents).join(','));
            }
            
            if (this.isCumulative) {
                params.set('cumulative', '1');
            }

            if (this.query.trim()) {
                params.set('q', this.query.trim());
            }

            if (this.viewMode !== 'grid') {
                params.set('view', this.viewMode);
            }

            if (this.sortMode !== 'featured') {
                params.set('sort', this.sortMode);
            }

            if (this.outsideTypologies) {
                params.set('outside', '1');
            }

            if (this.selectedStatus) {
                params.set('status', this.selectedStatus);
            }

            if (this.selectedSizeUnit !== 'sqft') {
                params.set('size_unit', this.selectedSizeUnit);
            }

            if (this.isResidenceSizeFilterActive()) {
                params.set('size_min', this.selectedSizeMinURLValue());
                params.set('size_max', this.selectedSizeMaxURLValue());
            }

            return params.toString();
        },
        
        clearAllFilters() {
            this.setFilter('all');
        },
        
        hasActiveChildren(parentFilter) {
            const children = this.filterHierarchy[parentFilter];
            return children?.some(childFilter => this.activeFilters.includes(childFilter)) || false;
        },
        
        getActiveChildCount(parentFilter) {
            const children = this.filterHierarchy[parentFilter];
            return children?.filter(childFilter => this.activeFilters.includes(childFilter)).length || 0;
        },
        
        updateActiveFilterClasses() {
            const filters = this.cachedElements.get('filters');
            const filterParents = this.cachedElements.get('filterParents');

            // Child / standalone filters (elements with `.filter`)
            filters.forEach(el => {
                const token = el.dataset.filter;
                const parent = this.parentMap ? this.parentMap[token] : null;
                const isSelected = this.activeFilters.includes(token) || (parent && this.activeFilters.includes(parent));
                el.classList.toggle("filter-active", isSelected);
                el.setAttribute("aria-pressed", isSelected);
            });

            // Parent pills (elements with `.filter-parent`)
            filterParents.forEach(el => {
                const token = el.dataset.filter;
                const children = this.filterHierarchy[token] || [];
                const isSelected = this.activeFilters.includes(token);
                const hasActiveChildren = children.some(child => this.activeFilters.includes(child));
                const isActive = isSelected || hasActiveChildren;

                el.classList.toggle("filter-active", isActive);
                el.classList.toggle("has-active-children", hasActiveChildren);
                el.setAttribute("aria-pressed", isActive);
            });
        },
        
        updateExpandedStates() {
            const filterParents = this.cachedElements.get('filterParents');
            const filterChildren = this.cachedElements.get('filterChildren');

            // Batch parent updates
            filterParents.forEach(el => {
                const filter = el.dataset.filter;
                const isExpanded = this.isParentExpanded(filter);
                el.classList.toggle("expanded", isExpanded);
                el.setAttribute("aria-expanded", isExpanded);
            });

            // Batch children updates
            filterChildren.forEach(el => {
                const parentFilter = el.dataset.parent;
                const isExpanded = this.isParentExpanded(parentFilter);

                el.classList.toggle('expanded', isExpanded);
                el.setAttribute("aria-hidden", !isExpanded);

                if (isExpanded) {
                    el.style.display = "block";
                    el.classList.remove('hide');
                    el.offsetHeight; // Trigger reflow
                    el.classList.add('show');
                } else {
                    el.classList.remove('show');
                    el.classList.add('hide');
                    setTimeout(() => {
                        if (!this.expandedParents.has(parentFilter)) {
                            el.style.display = "none";
                        }
                    }, 300);
                }
            });
        },

        syncTypologyFromActiveFilters() {
            const selectedFilters = this.activeFilters.filter(f => f !== 'all');

            if (selectedFilters.length > 1) {
                this.selectedTypology = 'all';
                this.selectedTypologyLabel = 'All';
                this.selectedSubTypology = '';
                this.selectedSubTypologyLabel = 'All';
                return;
            }

            const selected = selectedFilters[0] || 'all';
            const parent = this.parentMap[selected] || selected;
            this.selectedTypology = parent;
            this.selectedTypologyLabel = this.getFilterLabel(parent);
            this.selectedSubTypology = this.parentMap[selected] ? selected : '';
            if (this.selectedSubTypology) {
                const child = this.getChildTypologyOptions().find((opt) => opt.value === this.selectedSubTypology);
                this.selectedSubTypologyLabel = child?.label || this.getFilterLabel(this.selectedSubTypology);
            } else {
                this.selectedSubTypologyLabel = 'All';
            }
        },

        hydrateFacetOptions() {
            const items = this.getDiscoveryItems();
            const statuses = new Set();

            items.forEach(item => {
                const status = (item.dataset.status || '').trim();

                if (status) {
                    statuses.add(status);
                }
            });

            this.statusOptions = Array.from(statuses).sort((a, b) => a.localeCompare(b));

            if (this.selectedStatus && !this.statusOptions.includes(this.selectedStatus)) {
                this.selectedStatus = '';
            }
        },

        getChildTypologyOptions() {
            if (!this.selectedTypology || this.selectedTypology === 'all') {
                return [];
            }

            const mappedChildren = this.childTypologyOptionsByParent[this.selectedTypology];
            if (Array.isArray(mappedChildren) && mappedChildren.length > 0) {
                return mappedChildren.map((item) => ({
                    value: item.value,
                    label: item.label || item.value,
                }));
            }

            const children = this.filterHierarchy[this.selectedTypology] || [];
            return children.map((token) => ({
                value: token,
                label: this.filterLabels[token] || token,
            }));
        },

        getFilterLabel(token) {
            if (!token || token === 'all') {
                return 'All';
            }
            // Prefer child option labels if available
            for (const parent in this.childTypologyOptionsByParent) {
                const found = (this.childTypologyOptionsByParent[parent] || []).find((opt) => opt.value === token);
                if (found?.label) {
                    return found.label;
                }
            }
            const optionInDOM = this.$el
                ? this.$el.querySelector(`.discovery-dropdown__option[data-filter-token="${token}"]`)
                : null;
            if (optionInDOM && optionInDOM.textContent) {
                const domLabel = optionInDOM.textContent.trim();
                if (domLabel) {
                    return domLabel;
                }
            }
            return this.filterLabels[token] || token;
        },

        getTypologyLabel(token) {
            return this.getFilterLabel(token);
        },

        getSortLabel(mode) {
            const map = {
                date_desc: 'Date (Newest first)',
                date_asc: 'Date (Oldest first)',
                featured: 'Featured',
                az: 'A-Z',
            };
            return map[mode] || 'Featured';
        },

        matchesSelectedTypology(filterArray) {
            if (!Array.isArray(filterArray) || !filterArray.length) {
                return this.selectedTypology === 'all' && !this.selectedSubTypology;
            }

            const selectedFilters = this.activeFilters.filter(f => f !== 'all');
            if (selectedFilters.length > 1) {
                return this.matchesAnyActiveFilter(filterArray);
            }

            if (this.selectedSubTypology) {
                return filterArray.includes(this.selectedSubTypology);
            }

            if (!this.selectedTypology || this.selectedTypology === 'all') {
                return true;
            }

            return filterArray.includes(this.selectedTypology);
        },

        normalizeFacetValue(value) {
            return (value || '')
                .toString()
                .toLowerCase()
                .replace(/\s+/g, ' ')
                .trim();
        },

        matchesFacetValue(itemValue, selectedValue) {
            const selected = this.normalizeFacetValue(selectedValue);
            if (!selected) {
                return true;
            }

            const raw = (itemValue || '').toString();
            if (!raw.trim()) {
                return false;
            }

            const options = raw
                .split(/[;,|]/)
                .map((part) => this.normalizeFacetValue(part))
                .filter(Boolean);

            if (!options.length) {
                return false;
            }

            return options.some((option) => option === selected || option.includes(selected) || selected.includes(option));
        },

        matchesSizeValue(itemValue) {
            const numericValue = this.parseNumericValue(itemValue);
            if (numericValue === null) {
                return false;
            }

            if (!this.isResidenceSizeFilterActive()) {
                return true;
            }

            return numericValue >= this.selectedSizeMin && numericValue <= this.selectedSizeMax;
        },

        parseNumericValue(value) {
            if (value === null || value === undefined || value === '') {
                return null;
            }

            const normalized = value
                .toString()
                .replace(/,/g, '')
                .trim();

            if (normalized === '') {
                return null;
            }

            const numeric = Number.parseFloat(normalized);
            return Number.isFinite(numeric) ? numeric : null;
        },

        clampSizeValue(value, min = this.sizeRangeMin, max = this.sizeRangeMax, fallback = max) {
            if (!this.hasSizeRange()) {
                return null;
            }

            if (value === null) {
                return fallback;
            }

            return Math.min(max, Math.max(min, value));
        },

        serializeSizeValue(value) {
            if (value === null || value === undefined || !Number.isFinite(value)) {
                return '';
            }

            return Number.isInteger(value) ? String(value) : String(Number(value.toFixed(2)));
        },

        selectedSizeMinURLValue() {
            return this.serializeSizeValue(this.selectedSizeMin);
        },

        selectedSizeMaxURLValue() {
            return this.serializeSizeValue(this.selectedSizeMax);
        },

        hasSizeRange() {
            return Number.isFinite(this.sizeRangeMin) && Number.isFinite(this.sizeRangeMax);
        },

        hasActiveSizeFilter() {
            return this.hasSizeRange() && (
                !Number.isFinite(this.selectedSizeMin) ||
                !Number.isFinite(this.selectedSizeMax) ||
                this.selectedSizeMin > this.sizeRangeMin ||
                this.selectedSizeMax < this.sizeRangeMax
            );
        },

        isResidenceSizeFilterActive() {
            return this.sizeTab === 'residence' && this.hasActiveSizeFilter();
        },

        hydrateSizeRange(dataset = {}) {
            const items = this.getDiscoveryItems();
            const dataSizes = items
                .map((item) => this.parseNumericValue(item.dataset.sizeValue))
                .filter((value) => value !== null);

            const configuredMin = this.parseNumericValue(dataset.sizeRangeMin);
            const configuredMax = this.parseNumericValue(dataset.sizeRangeMax);
            const derivedMin = dataSizes.length ? Math.min(...dataSizes) : null;
            const derivedMax = dataSizes.length ? Math.max(...dataSizes) : null;

            let min = configuredMin ?? derivedMin;
            let max = configuredMax ?? derivedMax;

            if (min === null || max === null) {
                this.sizeRangeMin = null;
                this.sizeRangeMax = null;
                this.selectedSizeMin = null;
                this.selectedSizeMax = null;
                return;
            }

            if (max < min) {
                const swapped = min;
                min = max;
                max = swapped;
            }

            this.sizeRangeMin = min;
            this.sizeRangeMax = max;
            this.sizeRangeStep = [min, max, ...dataSizes].some((value) => !Number.isInteger(value)) ? 0.1 : 1;
            this.selectedSizeMin = this.clampSizeValue(this.selectedSizeMin, min, max, min);
            this.selectedSizeMax = this.clampSizeValue(this.selectedSizeMax, min, max, max);
            if (this.selectedSizeMin > this.selectedSizeMax) {
                this.selectedSizeMin = min;
                this.selectedSizeMax = max;
            }
        },

        sizeHeadingLabel() {
            const unit = this.selectedSizeUnit === 'sqm' ? 'SQM' : 'SQ FT';
            return `Filter Projects by ${unit}`;
        },

        formatSizeValue(value) {
            if (!Number.isFinite(value)) {
                return '';
            }

            return new Intl.NumberFormat('en-GB', {
                maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
            }).format(value);
        },

        sizeBoundaryLabel(value, withPlus = false) {
            const formatted = this.formatSizeValue(value);
            if (!formatted) {
                return '';
            }

            return withPlus ? `${formatted}+` : formatted;
        },

        sizeCurrentLabel() {
            if (!this.hasSizeRange()) {
                return 'All';
            }

            if (!this.hasActiveSizeFilter()) {
                return `${this.sizeBoundaryLabel(this.sizeRangeMin)} - ${this.sizeBoundaryLabel(this.sizeRangeMax, true)}`;
            }

            return `${this.sizeBoundaryLabel(this.selectedSizeMin)} - ${this.sizeBoundaryLabel(this.selectedSizeMax, this.selectedSizeMax === this.sizeRangeMax)}`;
        },

        sizeSelectionStyle() {
            if (!this.hasSizeRange()) {
                return '';
            }

            const span = this.sizeRangeMax - this.sizeRangeMin;
            if (span <= 0) {
                return 'left:0%; width:100%;';
            }

            const left = ((this.selectedSizeMin - this.sizeRangeMin) / span) * 100;
            const right = ((this.selectedSizeMax - this.sizeRangeMin) / span) * 100;

            return `left:${left}%; width:${Math.max(0, right - left)}%;`;
        },

        sizeHandleStyle(handle) {
            if (!this.hasSizeRange()) {
                return '';
            }

            const span = this.sizeRangeMax - this.sizeRangeMin;
            if (span <= 0) {
                return 'left:0%;';
            }

            const value = handle === 'max' ? this.selectedSizeMax : this.selectedSizeMin;
            const left = ((value - this.sizeRangeMin) / span) * 100;
            const offset = this.getSizeHandlePixelOffset(handle);

            return `left:calc(${left}% + ${offset}px);`;
        },

        sizeHandleLabelStyle(handle) {
            return this.sizeHandleStyle(handle);
        },

        sizeHandleLabel(handle) {
            const value = handle === 'max' ? this.selectedSizeMax : this.selectedSizeMin;
            return this.sizeBoundaryLabel(value, handle === 'max' && value === this.sizeRangeMax);
        },

        showSizeTooltip(mode = 'both') {
            if (!this.hasSizeRange()) {
                return;
            }

            if (this.sizeTooltipHideTimer) {
                window.clearTimeout(this.sizeTooltipHideTimer);
                this.sizeTooltipHideTimer = null;
            }

            this.sizeTooltipMode = ['min', 'max', 'both'].includes(mode) ? mode : 'both';
        },

        hideSizeTooltipSoon(delay = 220) {
            if (this.sizeTooltipHideTimer) {
                window.clearTimeout(this.sizeTooltipHideTimer);
            }

            this.sizeTooltipHideTimer = window.setTimeout(() => {
                this.sizeTooltipMode = '';
                this.sizeTooltipHideTimer = null;
            }, delay);
        },

        isSizeTooltipVisible(handle) {
            if (!this.sizeTooltipMode) {
                return false;
            }

            return this.sizeTooltipMode === 'both' || this.sizeTooltipMode === handle;
        },

        getSizeHandlePixelOffset(handle) {
            if (!this.hasSizeRange()) {
                return 0;
            }

            const span = this.sizeRangeMax - this.sizeRangeMin;
            if (span <= 0) {
                return handle === 'min' ? -10 : 10;
            }

            const distance = Math.abs(this.selectedSizeMax - this.selectedSizeMin);
            const threshold = Math.max(this.sizeRangeStep * 2, span * 0.03);

            if (distance > threshold) {
                return 0;
            }

            return handle === 'min' ? -10 : 10;
        },

        getSizeValueFromPointer(event) {
            const isLot = this.sizeTab === 'lot';
            const track = isLot ? this.$refs?.lotSizeSliderTrack : this.$refs?.sizeSliderTrack;
            const hasRange = isLot ? this.hasLotSizeRange() : this.hasSizeRange();
            const rangeMin = isLot ? this.lotSizeRangeMin : this.sizeRangeMin;
            const rangeMax = isLot ? this.lotSizeRangeMax : this.sizeRangeMax;
            const step = isLot ? this.lotSizeRangeStep : this.sizeRangeStep;

            if (!track || !hasRange) {
                return null;
            }

            const rect = track.getBoundingClientRect();
            const point = event.touches && event.touches[0]
                ? event.touches[0]
                : event.changedTouches && event.changedTouches[0]
                    ? event.changedTouches[0]
                    : event;

            if (!point || typeof point.clientX !== 'number') {
                return null;
            }

            const ratio = Math.min(1, Math.max(0, (point.clientX - rect.left) / rect.width));
            const rawValue = rangeMin + ((rangeMax - rangeMin) * ratio);
            const steppedValue = Math.round(rawValue / step) * step;

            return isLot
                ? this.clampLotSizeValue(steppedValue, rangeMin, rangeMax)
                : this.clampSizeValue(steppedValue, rangeMin, rangeMax);
        },

        syncActiveFiltersFromTypology() {
            this.expandedParents.clear();
            if (this.selectedSubTypology) {
                this.activeFilters = [this.selectedSubTypology];
                return;
            }

            this.activeFilters = this.selectedTypology === 'all'
                ? ['all']
                : [this.selectedTypology];
        },

        captureInitialDiscoveryState() {
            this.initialDiscoveryState = {
                activeFilters: [...this.activeFilters],
                query: this.query.trim(),
                viewMode: this.viewMode,
                sortMode: this.sortMode,
                selectedTypology: this.selectedTypology,
                selectedSubTypology: this.selectedSubTypology,
                selectedStatus: this.selectedStatus,
                selectedSizeUnit: this.selectedSizeUnit,
                selectedSizeMin: this.clampSizeValue(this.selectedSizeMin, this.sizeRangeMin, this.sizeRangeMax, this.sizeRangeMin),
                selectedSizeMax: this.clampSizeValue(this.selectedSizeMax, this.sizeRangeMin, this.sizeRangeMax, this.sizeRangeMax),
                selectedLotSizeMin: this.clampLotSizeValue(this.selectedLotSizeMin, this.lotSizeRangeMin, this.lotSizeRangeMax, this.lotSizeRangeMin),
                selectedLotSizeMax: this.clampLotSizeValue(this.selectedLotSizeMax, this.lotSizeRangeMin, this.lotSizeRangeMax, this.lotSizeRangeMax),
            };
        },

        getElementSizeValue(element) {
            if (!element || !element.dataset) {
                return null;
            }

            return this.parseNumericValue(element.dataset.sizeValue);
        },

        // ─── Lot Size Range ──────────────────────────────────────────────────────

        hasLotSizeRange() {
            return Number.isFinite(this.lotSizeRangeMin) && Number.isFinite(this.lotSizeRangeMax);
        },

        hasActiveLotSizeFilter() {
            return this.hasLotSizeRange() && (
                !Number.isFinite(this.selectedLotSizeMin) ||
                !Number.isFinite(this.selectedLotSizeMax) ||
                this.selectedLotSizeMin > this.lotSizeRangeMin ||
                this.selectedLotSizeMax < this.lotSizeRangeMax
            );
        },

        isLotSizeFilterActive() {
            return this.sizeTab === 'lot' && this.hasActiveLotSizeFilter();
        },

        clampLotSizeValue(value, min = this.lotSizeRangeMin, max = this.lotSizeRangeMax, fallback = max) {
            if (!this.hasLotSizeRange()) {
                return null;
            }

            if (value === null) {
                return fallback;
            }

            return Math.min(max, Math.max(min, value));
        },

        hydrateLotSizeRange(dataset = {}) {
            const items = this.getDiscoveryItems();
            const dataLotSizes = items
                .map((item) => this.parseNumericValue(item.dataset.lotSizeValue))
                .filter((value) => value !== null);

            const configuredMin = this.parseNumericValue(dataset.lotSizeRangeMin);
            const configuredMax = this.parseNumericValue(dataset.lotSizeRangeMax);
            const derivedMin = dataLotSizes.length ? Math.min(...dataLotSizes) : null;
            const derivedMax = dataLotSizes.length ? Math.max(...dataLotSizes) : null;

            let min = configuredMin ?? derivedMin;
            let max = configuredMax ?? derivedMax;

            if (min === null || max === null) {
                this.lotSizeRangeMin = null;
                this.lotSizeRangeMax = null;
                this.selectedLotSizeMin = null;
                this.selectedLotSizeMax = null;
                return;
            }

            if (max < min) {
                const swapped = min;
                min = max;
                max = swapped;
            }

            this.lotSizeRangeMin = min;
            this.lotSizeRangeMax = max;
            this.lotSizeRangeStep = [min, max, ...dataLotSizes].some((value) => !Number.isInteger(value)) ? 0.1 : 1;
            this.selectedLotSizeMin = this.clampLotSizeValue(this.selectedLotSizeMin, min, max, min);
            this.selectedLotSizeMax = this.clampLotSizeValue(this.selectedLotSizeMax, min, max, max);
            if (this.selectedLotSizeMin > this.selectedLotSizeMax) {
                this.selectedLotSizeMin = min;
                this.selectedLotSizeMax = max;
            }
        },

        matchesLotSizeValue(itemValue) {
            const numericValue = this.parseNumericValue(itemValue);
            if (numericValue === null) {
                return false;
            }

            if (!this.isLotSizeFilterActive()) {
                return true;
            }

            return numericValue >= this.selectedLotSizeMin && numericValue <= this.selectedLotSizeMax;
        },

        resetLotSizeFilter(shouldApply = true) {
            if (!this.hasLotSizeRange()) {
                return;
            }
            this.selectedLotSizeMin = this.lotSizeRangeMin;
            this.selectedLotSizeMax = this.lotSizeRangeMax;
            this.showSizeTooltip('both');
            this.hideSizeTooltipSoon();
            if (shouldApply) {
                this.applyDiscovery({ scrollToResults: true });
            }
        },

        lotSizeCurrentLabel() {
            if (!this.hasLotSizeRange()) {
                return 'All';
            }

            if (!this.hasActiveLotSizeFilter()) {
                return `${this.sizeBoundaryLabel(this.lotSizeRangeMin)} - ${this.sizeBoundaryLabel(this.lotSizeRangeMax, true)}`;
            }

            return `${this.sizeBoundaryLabel(this.selectedLotSizeMin)} - ${this.sizeBoundaryLabel(this.selectedLotSizeMax, this.selectedLotSizeMax === this.lotSizeRangeMax)}`;
        },

        lotSizeSelectionStyle() {
            if (!this.hasLotSizeRange()) {
                return '';
            }

            const span = this.lotSizeRangeMax - this.lotSizeRangeMin;
            if (span <= 0) {
                return 'left:0%; width:100%;';
            }

            const left = ((this.selectedLotSizeMin - this.lotSizeRangeMin) / span) * 100;
            const right = ((this.selectedLotSizeMax - this.lotSizeRangeMin) / span) * 100;

            return `left:${left}%; width:${Math.max(0, right - left)}%;`;
        },

        lotSizeHandleStyle(handle) {
            if (!this.hasLotSizeRange()) {
                return '';
            }

            const span = this.lotSizeRangeMax - this.lotSizeRangeMin;
            if (span <= 0) {
                return 'left:0%;';
            }

            const value = handle === 'max' ? this.selectedLotSizeMax : this.selectedLotSizeMin;
            const left = ((value - this.lotSizeRangeMin) / span) * 100;
            const offset = this.getLotSizeHandlePixelOffset(handle);

            return `left:calc(${left}% + ${offset}px);`;
        },

        lotSizeHandleLabel(handle) {
            const value = handle === 'max' ? this.selectedLotSizeMax : this.selectedLotSizeMin;
            return this.sizeBoundaryLabel(value, handle === 'max' && value === this.lotSizeRangeMax);
        },

        getLotSizeHandlePixelOffset(handle) {
            if (!this.hasLotSizeRange()) {
                return 0;
            }

            const span = this.lotSizeRangeMax - this.lotSizeRangeMin;
            if (span <= 0) {
                return handle === 'min' ? -10 : 10;
            }

            const distance = Math.abs(this.selectedLotSizeMax - this.selectedLotSizeMin);
            const threshold = Math.max(this.lotSizeRangeStep * 2, span * 0.03);

            if (distance > threshold) {
                return 0;
            }

            return handle === 'min' ? -10 : 10;
        },
    };
}
